// apps/client/src/components/widgets/TopLendersWidget.tsx
// Widget to display top lenders by volume, portfolio, or commission

import React, { useState, useMemo } from 'react';
import LenderPieChart from '../charts/LenderPieChart';
import LenderBarChart from '../charts/LenderBarChart';
import type { MonthlyAggregate } from '../../types';
import { formatCurrency, formatLoanBook } from '../../utils/formatters';
import { useHideCommissions } from '../../hooks/useHideCommissions';

interface TopLendersWidgetProps {
  aggregates: MonthlyAggregate[];
  title?: string;
  defaultLimit?: number;
  color?: string;
  /** Also drop the commission metric (an administrator denied the dashboard commission figures). */
  hideCommissionMetric?: boolean;
}

type MetricType = 'volume' | 'portfolio' | 'commission';
type ChartType = 'pie' | 'bar';
type LimitType = 5 | 10 | 'all';

interface LenderMetrics {
  lender: string;
  upfrontCommission: number;
  trailCommission: number;
  totalCommission: number;
  loanAmount: number;
  lineOfTrail: number;
  recordCount: number;
}

const TopLendersWidget: React.FC<TopLendersWidgetProps> = ({
  aggregates,
  title = 'Top Lenders',
  defaultLimit = 5,
  color = '#3b82f6',
  hideCommissionMetric = false,
}) => {
  // A hidden user's commission slices are all 0, so the commission metric would
  // open on a 0% chart; it is removed from the dropdown entirely and volume is
  // the default for them. `metric` is DERIVED rather than merely initialised,
  // because claims resolve asynchronously: a widget first rendered before
  // `hideCommissions` flips true would otherwise stay on 'commission'.
  const hideCommissions = useHideCommissions() || hideCommissionMetric;
  const [selectedMetric, setSelectedMetric] = useState<MetricType>('commission');
  const metric: MetricType =
    hideCommissions && selectedMetric === 'commission' ? 'volume' : selectedMetric;
  const [chartType, setChartType] = useState<ChartType>('pie');
  const [limit, setLimit] = useState<LimitType>(defaultLimit as LimitType);

  // Aggregate lender data across all months
  const lenderMetrics = useMemo(() => {
    const metricsMap = new Map<string, LenderMetrics>();

    aggregates.forEach(agg => {
      if (!agg.byLender) return;

      Object.entries(agg.byLender).forEach(([lender, data]) => {
        const existing = metricsMap.get(lender) || {
          lender,
          upfrontCommission: 0,
          trailCommission: 0,
          totalCommission: 0,
          loanAmount: 0,
          lineOfTrail: 0,
          recordCount: 0,
        };

        metricsMap.set(lender, {
          lender,
          upfrontCommission: existing.upfrontCommission + (data.upfrontNet || 0),
          trailCommission: existing.trailCommission + (data.trailNet || 0),
          totalCommission: existing.totalCommission + (data.upfrontNet || 0) + (data.trailNet || 0),
          loanAmount: existing.loanAmount + (data.totalLoanAmount || 0),
          lineOfTrail: Math.max(existing.lineOfTrail, data.lineOfTrail || 0), // Use max, not sum (snapshot data)
          recordCount: existing.recordCount + (data.recordCount || 0),
        });
      });
    });

    return Array.from(metricsMap.values());
  }, [aggregates]);

  // Get top lenders by selected metric
  const topLenders = useMemo(() => {
    // Determine value based on metric
    const getValue = (lender: LenderMetrics): number => {
      switch (metric) {
        case 'volume':
          return lender.loanAmount;
        case 'portfolio':
          return lender.lineOfTrail;
        case 'commission':
          return lender.totalCommission;
        default:
          return 0;
      }
    };

    // Sort by value descending
    const sorted = [...lenderMetrics].sort((a, b) => getValue(b) - getValue(a));

    // Apply limit
    const limitedData = limit === 'all' ? sorted : sorted.slice(0, limit);

    // Calculate "Other" rollup if needed
    const otherData = limit === 'all' ? [] : sorted.slice(limit);
    const otherValue = otherData.reduce((sum, lender) => sum + getValue(lender), 0);

    // Calculate total for percentages
    const total = sorted.reduce((sum, lender) => sum + getValue(lender), 0);

    // Format data for charts
    const chartData = limitedData.map(lender => ({
      lender: lender.lender,
      value: getValue(lender),
      percentage: total > 0 ? (getValue(lender) / total) * 100 : 0,
    }));

    // Add "Other" if needed
    if (otherValue > 0 && limit !== 'all') {
      chartData.push({
        lender: 'Other',
        value: otherValue,
        percentage: total > 0 ? (otherValue / total) * 100 : 0,
      });
    }

    return chartData;
  }, [lenderMetrics, metric, limit]);

  // Format value based on metric
  const formatValue = (value: number): string => {
    switch (metric) {
      case 'volume':
        return formatLoanBook(value);
      case 'portfolio':
        return value.toLocaleString() + ' loans';
      case 'commission':
        return formatCurrency(value);
      default:
        return String(value);
    }
  };

  return (
    <div className="bg-gray-800 p-6 rounded-lg shadow-lg relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-10"
        style={{ background: `radial-gradient(circle at 50% 0%, ${color}40 0%, transparent 70%)` }}
      />

      <div className="relative z-10">
        {/* Header with controls */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <h3 className="font-bold text-lg text-white">{title}</h3>

          <div className="flex flex-wrap items-center gap-3">
            {/* Metric selector */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-400">Metric:</label>
              <select
                value={metric}
                onChange={(e) => setSelectedMetric(e.target.value as MetricType)}
                className="bg-gray-700 border-gray-600 text-white rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {!hideCommissions && <option value="commission">Commission</option>}
                <option value="volume">Volume</option>
                <option value="portfolio">Portfolio</option>
              </select>
            </div>

            {/* Limit selector */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-400">Show:</label>
              <select
                value={limit}
                onChange={(e) => {
                  const val = e.target.value;
                  setLimit(val === 'all' ? 'all' : parseInt(val) as LimitType);
                }}
                className="bg-gray-700 border-gray-600 text-white rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={5}>Top 5</option>
                <option value={10}>Top 10</option>
                <option value="all">All</option>
              </select>
            </div>

            {/* Chart type toggle */}
            <div className="flex gap-1 bg-gray-700 rounded-md p-1">
              <button
                onClick={() => setChartType('pie')}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  chartType === 'pie'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                Pie
              </button>
              <button
                onClick={() => setChartType('bar')}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  chartType === 'bar'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                Bar
              </button>
            </div>
          </div>
        </div>

        {/* Chart */}
        {topLenders.length === 0 ? (
          <div className="p-8 bg-gray-700 rounded text-center">
            <p className="text-gray-400">No lender data available for the selected period.</p>
          </div>
        ) : chartType === 'pie' ? (
          <LenderPieChart
            data={topLenders}
            title=""
            color={color}
            formatValue={formatValue}
          />
        ) : (
          <LenderBarChart
            data={topLenders}
            title=""
            color={color}
            formatValue={formatValue}
          />
        )}

        {/* Summary stats */}
        {topLenders.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-700">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-xs text-gray-400 uppercase">Total Lenders</p>
                <p className="text-lg font-semibold text-white">{lenderMetrics.length}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase">Showing</p>
                <p className="text-lg font-semibold text-white">
                  {limit === 'all' ? 'All' : `Top ${limit}`}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase">Chart Type</p>
                <p className="text-lg font-semibold text-white capitalize">{chartType}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase">Metric</p>
                <p className="text-lg font-semibold text-white capitalize">{metric}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopLendersWidget;
